import React, { useState, useCallback, useMemo, useReducer, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Grid,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Dialog
} from '@material-ui/core';
import { DatePicker } from '@material-ui/pickers';
import { Search, Clear, AutoAwesome } from '@material-ui/icons';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import { Map as MapIcon, Schedule as TimelineIcon, Close, Fullscreen, FullscreenExit, Assessment } from '@material-ui/icons';
import { addDays, subDays } from 'date-fns';
import moment from 'moment';

// Hooks
import { useRouteScheduling } from './hooks/useRouteScheduling';
import { useMapControls } from './hooks/useMapControls';
import { useCrewManagement } from './hooks/useCrewManagement';
import { useWeeklySummary } from './hooks/useRouteSchedulingEnhancements';

// Components
import CrewList from './components/CrewList';
import CalendarPanel from './components/CalendarPanel';
import RouteMap from './components/RouteMap';
import CrewDayTimeline from './components/CrewDayTimeline';
import SelectedCrewDetails from './components/SelectedCrewDetailsContainer';
import JobsSidebar from './components/JobsSidebar';
import UnassignmentConfirmationModal from './components/ConfirmUnassignmentDialog';
import AssignmentSuggestionsDialog from './components/AssignmentSuggestionsDialog';
import RouteErrorBoundary from './components/RouteErrorBoundary';
import WeeklySummaryPanel from './components/WeeklySummaryPanel';
import CrewEfficiencyDashboard from './components/CrewEfficiencyDashboard';
import ScheduleHistoryViewer from './components/ScheduleHistoryViewer';
import UndoRedoControls from './components/UndoRedoControls';
import AssignmentProgressDialog from './components/AssignmentProgressDialog';

// Services
import { RouteService } from './services/RouteService';
import { CrewService } from './services/CrewService';
import { NotificationService } from './services/NotificationService';

// Utils
import { filterCrews, calculateHours, getCrewColor } from './utils/crewUtils';
import { validateRouteData } from './utils/routeValidation';
import { ROUTE_CONFIG } from './config/routeConfig';

// Type definitions
import { CrewsPropType, WeeklyRoutePropType } from './types/routeTypes';

// Styles
import { useStyles } from './DailyScheduleMapViewStyles';

// State management
import { routeReducer, initialState } from './state/routeReducer';
import { 
  setLoading, 
  setRoutes, 
  selectCrew, 
  updateFilters,
  setActiveMarker,
  setHighlightedStop,
  updateUI,
  setCrews
} from './state/routeActions';
import { scheduleHistory } from './utils/scheduleHistory';

const DailyScheduleMapView = (props) => {
  const classes = useStyles();
    
  // Local UI state (doesn't need to be in global state)
  const [viewMode, setViewMode] = useState('map');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showAllSitesOnMap, setShowAllSitesOnMap] = useState(false);
  const [historyViewerOpen, setHistoryViewerOpen] = useState(false);
  const [siteFilterType, setSiteFilterType] = useState('all'); // 'all', 'assigned', 'unassigned'
  const [highlightedSite, setHighlightedSite] = useState(null); // For visual highlighting only
  // const [jobSiteDetails, setJobSiteDetails] = useState(null);

  // Extract state values
  // const {
  //   loading,
  //   selectedDate,
  //   crews,
  //   sites,
  //   weeklyRoutes,
  //   weeklyHours,
  //   filters,
  //   ui,
  //   cache
  // } = state;
  
  // Services (memoize to prevent recreating on every render)
  const routeService = useMemo(() => new RouteService(props.url), [props.url]);
  const crewService = useMemo(() => new CrewService(props.url), [props.url]);
  const notificationService = useMemo(() => 
    new NotificationService(props.notification), 
    [props.notification]
  );

  const processedCrews = useMemo(() => {
    if (!props.crews?.length) return [];
    
    const uniqueCrewIds = [...new Set(props.crews.map(crew => crew._id))];
    return uniqueCrewIds.map((crewId, index) => {
      const crewItem = props.crews.find(crew => crew._id === crewId);
      const crewColor = getCrewColor(index); // This uses the color palette from config
      
      if (crewItem) {
        return {
          ...crewItem,
          name: `${crewItem.name}${crewItem.foremanName ? ` (${crewItem.foremanName})` : ''}`,
          color: crewColor.backgroundColor,
          textColor: crewColor.textColor
        };
      }
      return null;
    }).filter(Boolean);
  }, [props.crews]);
  
  // Custom hooks for specific functionality
  const {
    filteredMarkers,
    allSitesMarkers,
    unassignedJobs,
    activeRouteDetails,
    fetchRoutes,
    handleAssignToCrew,
    handleUnassignFromCrew,
    getDetailsAboutSite,
    jobSiteDetails,
    loading,
    selectedDate,
    crews,
    sites,
    weeklyRoutes,
    weeklyHours,
    filters,
    ui,
    cache,
    state,
    dispatch,
    handleClickAssign,
    handleClickOnSiteNearby,
    handleDateClick,
    sseProgress,
    isAssigning,
    fetchOppositeWeekData
  } = useRouteScheduling({
    ...props,
    crews: processedCrews
  });
  // const selectedDate = filters?.selectedDate || new Date()
  // console.log('Got sites??', sites)
  // console.log('Do we have crews??', crews)
  // const jobSiteDetails = state.jobSiteDetails;

  const {
    mapRef,
    center,
    zoomLevel,
    handleMapLoad,
    updateMapCenter,
    updateZoomLevel,
    isLoaded
  } = useMapControls({
    initialCenter: ui.center,
    initialZoom: ui.zoomLevel
  });
  
  const {
    filteredCrews,
    selectedCrew,
    handleCrewSelect,
    handleCrewNavigation,
    calculateCrewHours,
  } = useCrewManagement({
    crews,
    weeklyHours,
    filters,
    dispatch
  });
  // console.log('166...filters', filters)
  // Memoized calculations
  const dayOfWeek = useMemo(() => moment(selectedDate).format('dddd'), [selectedDate]);
  const weekOfTheYear = useMemo(() => moment(selectedDate).week(), [selectedDate]);
  const thisWeek = useMemo(() => weekOfTheYear % 2 === 0 ? 'Even' : 'Odd', [weekOfTheYear]);
  
  const totalFilteredHours = useMemo(() => {
    return filteredMarkers.reduce((sum, marker) => sum + Number(marker?.estimatedHours || 0), 0);
  }, [filteredMarkers]);

  // Use our enhanced weekly summary hook
  const weeklySummary = useWeeklySummary(weeklyRoutes, crews);
  
  // Filter allSitesMarkers based on the filter type (All/Assigned/Unassigned)
  const filteredAllSitesMarkers = useMemo(() => {
    if (!allSitesMarkers) return [];
    
    let filtered = [];
    switch (siteFilterType) {
      case 'assigned':
        filtered = allSitesMarkers.filter(marker => !marker.isUnassigned && marker.crew);
        break;
      case 'unassigned':
        filtered = allSitesMarkers.filter(marker => marker.isUnassigned || !marker.crew);
        break;
      case 'all':
      default:
        filtered = allSitesMarkers;
    }
    
    // Always include the highlighted site if it exists and isn't already in the filtered list
    if (highlightedSite && !filtered.find(m => m._id === highlightedSite._id)) {
      // Find the highlighted site in all markers
      const highlightedMarker = allSitesMarkers.find(m => m._id === highlightedSite._id);
      if (highlightedMarker) {
        filtered = [...filtered, highlightedMarker];
      }
    }
    
    return filtered;
  }, [allSitesMarkers, siteFilterType, highlightedSite]);
  
  // Ensure highlighted site is always visible on the map
  const markersToDisplay = useMemo(() => {
    const baseMarkers = showAllSitesOnMap ? filteredAllSitesMarkers : filteredMarkers;
    
    // If we have a highlighted site, ensure it's included
    if (highlightedSite && !baseMarkers.find(m => m._id === highlightedSite._id)) {
      // Find the highlighted site in all available sites
      const highlightedMarker = sites.find(s => s._id === highlightedSite._id);
      if (highlightedMarker) {
        // Add crew color if it has one
        const crew = crews.find(c => c._id === highlightedMarker.crew);
        const markerWithColor = {
          ...highlightedMarker,
          color: crew?.color || '#ff5722',
          crewName: crew?.name || 'Unassigned',
          isUnassigned: !highlightedMarker.crew
        };
        return [...baseMarkers, markerWithColor];
      }
    }
    
    return baseMarkers;
  }, [showAllSitesOnMap, filteredAllSitesMarkers, filteredMarkers, highlightedSite, sites, crews]);
  
  // Handlers
  const handleDateChange = useCallback(async (date) => {
    console.log('handleDateChange...', date)
    if (!date) {
      console.error('handleDateChange called without date');
      return;
    }
    
    // Update selected date in UI immediately
    dispatch(updateUI({ selectedDate: date }));
    
    // Only fetch routes if date is outside current week range
    // fetchRoutes will check internally if we need to load new data
    await fetchRoutes(date);
  }, [fetchRoutes, dispatch]);

  const handlePreviousDay = useCallback(() => {
    handleDateChange(subDays(selectedDate, 1));
  }, [selectedDate, handleDateChange]);
  
  const handleNextDay = useCallback(() => {
    console.log('handleNextDay...', selectedDate)
    handleDateChange(addDays(selectedDate, 1));
  }, [selectedDate, handleDateChange]);
  
  const handleSearchChange = useCallback((event) => {
    dispatch(updateFilters({ searchTerm: event.target.value }));
  }, []);
  
  const clearSearch = useCallback(() => {
    dispatch(updateFilters({ searchTerm: '' }));
  }, []);

  const handleSuggestionSelect = useCallback((suggestionData) => {
    const { crew, date, site } = suggestionData;
    handleDateChange(date);
    handleCrewSelect(crew);
    dispatch(setActiveMarker(site));
  }, [handleDateChange, handleCrewSelect]);
  
  const handleTimelineItemClick = useCallback((stop, crewId) => {
    const crew = crews.find(c => c._id === crewId);
    if (crew) {
      handleCrewSelect(crew);
      dispatch(setActiveMarker(stop));
      if (stop.latitude && stop.longitude) {
        updateMapCenter({
          lat: stop.latitude - ROUTE_CONFIG.MAP.OFFSET,
          lng: stop.longitude
        });
        updateZoomLevel(ROUTE_CONFIG.MAP.ZOOM_LEVELS.DETAIL);
      }
    }
  }, [crews, handleCrewSelect, updateMapCenter, updateZoomLevel]);
  
  // Initialize on mount with correct date
  React.useEffect(() => {
    // Use selectedDate from state to ensure consistency
    console.log('🚀 Initial mount - fetching routes for:', selectedDate);
    fetchRoutes(selectedDate);
  }, []); // Empty dependency array - only run once
  
  // Update crews if they change in Redux
  React.useEffect(() => {
    if (props.crews && props.crews.length > 0) {
      // Process the crews with colors and formatting
      const processedCrews = crewService.processCrews(props.crews);
      dispatch(setCrews(processedCrews));
    }
  }, [props.crews, crewService, dispatch]);
  
  const handleClickOnScheduledSite = (newSite) => {
    console.log('handleClickOnScheduledSite...', newSite)
    // dispatch(setActiveMarker(newSite));

    // getDetailsAboutSite(newSite);
  }
  const handleClearActiveMarker = useCallback(() => {
    dispatch(setActiveMarker(null));
  }, [dispatch]);

  const setActiveMarkerHandler = useCallback((marker) => {
    dispatch(setActiveMarker(marker));
  }, [dispatch]);

  // Extract timeline view check
  const isTimelineView = viewMode === 'timeline';
  const isAnalyticsView = viewMode === 'analytics';
  const isFullWidthView = isTimelineView || isAnalyticsView;
  
  console.log('Find our crews stops in htis: ', filteredMarkers)
  return (
    <RouteErrorBoundary>
      <div className={classes.root}>
        {/* Dialogs */}
        <UnassignmentConfirmationModal
          open={ui.isUnassignmentModalOpen}
          onClose={() => dispatch(updateUI({ isUnassignmentModalOpen: false }))}
          onConfirmUnassignment={(cancelRecurring, site) => {
            console.log('onConfirmUnassignment...', cancelRecurring, site)
            handleUnassignFromCrew(site, selectedCrew, selectedDate);
            dispatch(updateUI({ isUnassignmentModalOpen: false }));
          }}
          activeSite={filters.activeMarker}
        />
        
        <AssignmentSuggestionsDialog
          open={showSuggestions}
          onClose={() => setShowSuggestions(false)}
          activeMarker={filters.activeMarker}
          crews={crews}
          weeklyRoutes={weeklyRoutes}
          weeklyHours={weeklyHours}
          selectedDate={selectedDate}
          sites={sites}
          onSelectSuggestion={handleSuggestionSelect}
        />
        
        <AssignmentProgressDialog
          open={isAssigning}
          progress={sseProgress}
          onClose={() => {}} // Can't close during assignment
        />
        
        <Grid container spacing={0} style={{ height: '100vh', margin: 0, padding: 0, width: '100%' }}>
          {/* Left Panel - Calendar & Crews */}
          {!isFullWidthView && (
            <Grid item style={{ width: '25%', height: '100%', display: 'flex', flexDirection: 'column', padding: 0 }}>
              <CalendarPanel
                selectedDate={selectedDate}
                onDateChange={(newDate) => handleDateChange(newDate || new Date())}
                onPreviousDay={handlePreviousDay}
                onNextDay={handleNextDay}
                totalSites={sites.length}
                filteredStops={filteredMarkers.length}
                filteredHours={totalFilteredHours}
                dayOfWeek={dayOfWeek}
                weekNumber={weekOfTheYear}
                weekType={thisWeek}
              />
                            <WeeklySummaryPanel
                weeklyRoutes={weeklyRoutes}
                crews={crews}
                sites={sites}
                selectedDate={selectedDate}
              />
              <CrewList
                crews={filteredCrews}
                selectedCrew={selectedCrew}
                weeklyHours={weeklyHours}
                weeklyRoutes={weeklyRoutes}
                dayOfWeek={dayOfWeek}
                searchTerm={filters.searchTerm}
                onSearchChange={handleSearchChange}
                onClearSearch={clearSearch}
                onCrewSelect={handleCrewSelect}
                onDayClick={(crew, day) => {
                  const date = moment(selectedDate).day(day).toDate();
                  handleDateChange(date);
                  handleCrewSelect(crew);
                }}
              />
              

            </Grid>
          )}
          
          {/* Center Panel - Map/Timeline/Analytics */}
          <Grid item style={{ width: isFullWidthView ? '100%' : '41.67%', height: '100%', padding: 0 }}>
            <Paper className={classes.paper}>
              {/* View Toggle */}
              <div className={classes.viewToggleContainer}>
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={(e, newMode) => newMode && setViewMode(newMode)}
                  size="small"
                >
                  <ToggleButton value="map">
                    <MapIcon /> Map View
                  </ToggleButton>
                  <ToggleButton value="timeline">
                    <TimelineIcon /> Timeline View
                  </ToggleButton>
                  <ToggleButton value="analytics">
                    <Assessment /> Analytics
                  </ToggleButton>
                </ToggleButtonGroup>
                {viewMode === 'timeline' && (
                  <IconButton onClick={() => setIsFullscreen(!isFullscreen)} size="small">
                    {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
                  </IconButton>
                )}
              </div>
              
              {/* Map View */}
              {viewMode === 'map' ? (
                <div className={classes.mapContainer}>
                  <RouteMap
                    mapRef={mapRef}
                    center={center}
                    zoomLevel={zoomLevel}
                    loading={loading}
                    isLoaded={isLoaded}
                    filteredMarkers={markersToDisplay}
                    activeMarker={filters.activeMarker}
                    highlightedStop={filters.highlightedStop}
                    highlightedSite={highlightedSite}
                    selectedCrew={selectedCrew}
                    onMapLoad={handleMapLoad}
                    onMarkerClick={(marker) => {
                      // console.log('set selectd crew??', marker)
                      const crew = crews.find(c => c._id === marker.crew);
                      if (crew) {
                        handleCrewSelect(crew);
                      }
                      if (marker.isUnassigned || !marker.crew) {
                        // For unassigned markers, just highlight them (don't activate)
                        setHighlightedSite(marker);
                      } else {
                        // It's a crew stop
                        dispatch(setHighlightedStop(marker._id));
                      }

                    }}
                  />
                  {selectedCrew && (
                    <>
                    <div className={classes.selectedCrewContainer}>
                      <SelectedCrewDetails
                        crew={selectedCrew}
                        date={selectedDate}
                        stops={filteredMarkers}
                        activeMarker={filters.activeMarker}
                        highlightedStop={filters.highlightedStop}
                        jobSiteDetails={jobSiteDetails}
                        weeklyHours={weeklyHours}
                        weeklyRoutes={weeklyRoutes}
                        loading={loading}
                        handleUnassignment={(site) => {
                          dispatch(updateUI({ isUnassignmentModalOpen: true }));
                          dispatch(setActiveMarker(site));
                        }}
                        handleAssignToCrew={handleAssignToCrew}
                        handleChangeCrew={(direction) => handleCrewNavigation(direction)}
                        handleClickOnScheduledSite={(site) => {
                          dispatch(setHighlightedStop(site._id));
                        }}
                        activeRouteDetails={activeRouteDetails}
                        closeCrewDetails={() => {
                          dispatch(selectCrew(null));
                          dispatch(setHighlightedStop(null));
                        }}
                        crewRoute={weeklyRoutes.find(r => r.day === dayOfWeek)?.routes.find(r => r.crewId === selectedCrew._id)}
                        futureVisits={state.cache.futureVisits || []}
                        markers={sites}
                        url={props.url}
                        notification={props.notification}
                        fetchOppositeWeekData={fetchOppositeWeekData}
                      />
                    </div>
                   
                </>
                  )}
                  {/* {selectedCrew && (
                    <>
                      <SelectedCrewDetails
                        crew={selectedCrew}
                        date={selectedDate}
                        stops={filteredMarkers}
                        activeMarker={filters.activeMarker}
                        weeklyHours={weeklyHours}
                        loading={loading}
                        onUnassign={() => dispatch(updateUI({ isUnassignmentModalOpen: true }))}
                        onAssign={handleAssignToCrew}
                        onNavigate={handleCrewNavigation}
                        onClose={handleShowAllCrews}
                      />
                      
                      <div className={classes.mapOverlayActionArea}>
                        <Button variant="contained" color="secondary" onClick={handleShowAllCrews}>
                          Show All Crews
                        </Button>
                      </div>
                    </>
                  )} */}
                </div>
              ) : viewMode === 'timeline' ? (
                // Timeline View
                <CrewDayTimeline
                  selectedDate={selectedDate}
                  crews={filteredCrews}
                  weeklyRoutes={weeklyRoutes}
                  activeMarker={filters.activeMarker}
                  selectedCrew={selectedCrew}
                  onDateChange={handleDateChange}
                  onItemClick={handleTimelineItemClick}
                  sites={sites}
                />
              ) : (
                // Analytics View
                <CrewEfficiencyDashboard
                  weeklyRoutes={weeklyRoutes}
                  crews={filteredCrews}
                  selectedDate={selectedDate}
                  onCrewSelect={handleCrewSelect}
                  onDateChange={handleDateChange}
                  sites={sites}
                  handleAssignToCrew={handleAssignToCrew}
                  handleUnassignFromCrew={handleUnassignFromCrew}
                  setViewMode={setViewMode}
                  dispatch={dispatch}
                />
              )}
            </Paper>
          </Grid>
          
          {/* Right Panel - Jobs */}
          {!isFullWidthView && (
            <Grid item style={{ width: '33.33%', height: '100%', padding: 0 }}>
              <JobsSidebar
                tabValue={ui.tabValue}
                unassignedJobs={unassignedJobs}
                sites={sites}
                crews={crews}
                activeMarker={filters.activeMarker}
                selectedCrew={selectedCrew}
                selectedDate={selectedDate}
                onShowSuggestions={() => setShowSuggestions(true)}
                onSelectJob={(job) => {
                  dispatch(setActiveMarker(job));
                  getDetailsAboutSite(job);
                }}
                onClearActiveMarker={handleClearActiveMarker} 
                setActiveMarker={setActiveMarkerHandler}
                jobSiteDetails={jobSiteDetails}
                handleClickAssign={handleClickAssign}
                handleUnassignFromCrew={(site) => {
                  dispatch(updateUI({ isUnassignmentModalOpen: true }));
                  dispatch(setActiveMarker(site));
                }}
                handleClickOnSiteNearby={handleClickOnSiteNearby}
                handleDateClick={handleDateClick}
                weeklyRoutes={weeklyRoutes}
                filteredCrews={filteredCrews}
                highlightedNearbySite={ui.highlightedNearbySite}
                setTabValue={(value) => dispatch(updateUI({ tabValue: value }))}
                onShowAllSitesToggle={setShowAllSitesOnMap}
                siteFilterType={siteFilterType}
                onSiteFilterTypeChange={setSiteFilterType}
                highlightedSite={highlightedSite}
                onHighlightSite={setHighlightedSite}
                url={props.url}
              />
            </Grid>
          )}
        </Grid>
        
        {/* Fullscreen Timeline Dialog */}
        <Dialog fullScreen open={isFullscreen} onClose={() => setIsFullscreen(false)}>
          <div className={classes.fullscreenContainer}>
            <IconButton onClick={() => setIsFullscreen(false)} className={classes.fullscreenCloseButton}>
              <Close />
            </IconButton>
            <CrewDayTimeline
              selectedDate={selectedDate}
              crews={filteredCrews}
              weeklyRoutes={weeklyRoutes}
              activeMarker={filters.activeMarker}
              selectedCrew={selectedCrew}
              onDateChange={handleDateChange}
              onItemClick={handleTimelineItemClick}
              fullscreen={true}
              sites={sites}
            />
          </div>
        </Dialog>

        {/* History Viewer */}
        <ScheduleHistoryViewer
          open={historyViewerOpen}
          onClose={() => setHistoryViewerOpen(false)}
          onRestore={async (restoredState, historyItem) => {
            console.log('Restoring schedule to:', historyItem);
            // Apply the restored state
            // This would typically update your Redux state or make API calls
            // For now, we'll just refresh
            handleDateChange(selectedDate);
            notificationService.success(`Restored to: ${historyItem.description}`);
          }}
          currentState={{
            weeklyRoutes,
            crews: filteredCrews,
            sites,
            selectedDate
          }}
        />

        {/* Undo/Redo Controls */}
        {viewMode === 'map' && (
          <UndoRedoControls
            onUndo={(lastChange) => {
              console.log('Undo:', lastChange);
              // Implement undo logic
              handleDateChange(selectedDate); // Refresh
            }}
            onRedo={() => {
              console.log('Redo');
              // Implement redo logic
            }}
            onShowHistory={() => setHistoryViewerOpen(true)}
            onSaveCheckpoint={() => {
              scheduleHistory.createRestorePoint(
                {
                  weeklyRoutes,
                  crews: filteredCrews,
                  sites,
                  selectedDate
                },
                `Checkpoint - ${moment().format('MMM D, h:mm A')}`
              );
              notificationService.success('Checkpoint saved');
            }}
          />
        )}
      </div>
    </RouteErrorBoundary>
  );
};

const mapStateToProps = (state) => {
  const maintenanceCrews = state.crews.filter(c => 
    c.division === 'Maintenance' &&
    c?.entity?.toString()?.includes('640a56404965b71a9edd0e7d') &&
    !c?.name?.toLowerCase()?.includes('test') && 
    !c?.crewOptions?.includes('npkwy') && 
    !c?.crewOptions?.includes('adot')
  );
  
  return {
    url: state.url,
    user: state.user,
    crews: maintenanceCrews,
    notification: state.notification
  };
};

// PropTypes validation for better development experience
DailyScheduleMapView.propTypes = {
  // From Redux
  url: PropTypes.string.isRequired,
  user: PropTypes.object,
  crews: CrewsPropType, // Using our defined type
  notification: PropTypes.func.isRequired,
  // Any other props the component might receive
};

export default connect(mapStateToProps)(DailyScheduleMapView);
